import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type {
    SceneDesignsListResponse,
    SceneDesignsRetrieveResponse,
    TemplatesListResponse,
    TemplatesRetrieveResponse,
} from 'js/src/client';
import { clearSceneDesignsForTemplates } from 'js/src/data/templates/templates-actions';
import type { RootState } from 'js/src/libs/RootState';

export enum APIRequestStatus {
    PENDING = 'PENDING',
    FETCHING = 'FETCHING',
    FETCHED = 'FETCHED',
    FAILED = 'FAILED',
}

type InitialState = {
    templatesListRequest: APIRequestStatus;
    templateCategoriesListRequest: APIRequestStatus;
    templateRetrieveRequests: Record<string, APIRequestStatus>;
    sceneDesignsListRequest: Record<string, APIRequestStatus>;
    sceneDesignRetrieveRequests: Record<string, APIRequestStatus>;
    displayGroupsListRequest: Record<string, APIRequestStatus>;
};

const initialState: InitialState = {
    templatesListRequest: APIRequestStatus.PENDING,
    templateCategoriesListRequest: APIRequestStatus.PENDING,
    templateRetrieveRequests: {},
    sceneDesignsListRequest: {},
    sceneDesignRetrieveRequests: {},
    displayGroupsListRequest: {},
};

const loadingInfoSlice = createSlice({
    name: 'loadingInfo',
    initialState,
    reducers: {
        setTemplatesListRequestStatus: (
            state,
            action: PayloadAction<{
                status: APIRequestStatus;
                data?: Awaited<TemplatesListResponse>;
            }>,
        ) => {
            const { status, data: templates } = action.payload;
            state.templatesListRequest = status;
            if (status !== APIRequestStatus.FETCHED) {
                return;
            }
            if (!templates) {
                throw new Error('Templates data is missing');
            }
            for (const template of templates) {
                state.templateRetrieveRequests[template.id] = APIRequestStatus.FETCHED;
            }
        },
        setTemplateCategoriesListRequestStatus: (
            state,
            action: PayloadAction<{ status: APIRequestStatus }>,
        ) => {
            state.templateCategoriesListRequest = action.payload.status;
        },
        setTemplateRetrieveRequestStatus: (
            state,
            action: PayloadAction<{
                id: string;
                status: APIRequestStatus;
                data?: Awaited<TemplatesRetrieveResponse>;
            }>,
        ) => {
            const { id, status, data: template } = action.payload;
            state.templateRetrieveRequests[id] = status;
            if (status !== APIRequestStatus.FETCHED) {
                return;
            }
            if (!template) {
                throw new Error('Template data is missing');
            }
            state.templateRetrieveRequests[id] = APIRequestStatus.FETCHED;
        },
        setSceneDesignsListRequestStatus: (
            state,
            action: PayloadAction<{
                id: string;
                status: APIRequestStatus;
                data?: Awaited<SceneDesignsListResponse>;
            }>,
        ) => {
            const { status, id: templateId, data: sceneDesigns } = action.payload;
            state.sceneDesignsListRequest[templateId] = status;
            if (status !== APIRequestStatus.FETCHED) {
                return;
            }
            if (!sceneDesigns) {
                throw new Error('SceneDesigns data is missing');
            }
            for (const sceneDesign of sceneDesigns) {
                state.sceneDesignRetrieveRequests[sceneDesign.id] = APIRequestStatus.FETCHED;
            }
        },
        setSceneDesignRetrieveRequestStatus: (
            state,
            action: PayloadAction<{
                id: string;
                status: APIRequestStatus;
                data?: Awaited<SceneDesignsRetrieveResponse>;
            }>,
        ) => {
            const { id, status, data: sceneDesign } = action.payload;
            state.sceneDesignRetrieveRequests[id] = status;
            if (status !== APIRequestStatus.FETCHED) {
                return;
            }
            if (!sceneDesign) {
                throw new Error('SceneDesign data is missing');
            }
            state.sceneDesignRetrieveRequests[id] = APIRequestStatus.FETCHED;
        },
        setDisplayGroupsListRequestStatus: (
            state,
            action: PayloadAction<{ id: string; status: APIRequestStatus }>,
        ) => {
            const { id: templateId, status } = action.payload;
            state.displayGroupsListRequest[templateId] = status;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(clearSceneDesignsForTemplates, (state, action) => {
            const { templateIds } = action.payload;
            for (const templateId of templateIds) {
                delete state.sceneDesignsListRequest[templateId];
            }
            const sceneDesignIdsToRemove = Object.keys(state.sceneDesignRetrieveRequests).filter(
                (sceneDesignId) => {
                    const templateId = sceneDesignId.split(':')[0];
                    return templateIds.includes(templateId);
                },
            );
            for (const sceneDesignId of sceneDesignIdsToRemove) {
                delete state.sceneDesignRetrieveRequests[sceneDesignId];
            }
        });
    },
    selectors: {
        isTemplatesListRequestPending: (state) => {
            return state.templatesListRequest === APIRequestStatus.PENDING;
        },
        isTemplatesListRequestFetching: (state) => {
            return state.templatesListRequest === APIRequestStatus.FETCHING;
        },
        isTemplatesListRequestFetched: (state) => {
            return state.templatesListRequest === APIRequestStatus.FETCHED;
        },
        isTemplatesListRequestFailed: (state) => {
            return state.templatesListRequest === APIRequestStatus.FAILED;
        },

        isTemplateCategoriesListRequestFetching: (state) => {
            return state.templateCategoriesListRequest === APIRequestStatus.FETCHING;
        },
        isTemplateCategoriesListRequestFetched: (state) => {
            return state.templateCategoriesListRequest === APIRequestStatus.FETCHED;
        },

        isTemplateRetrieveRequestPending: (state, id: string) => {
            return state.templateRetrieveRequests[id] === APIRequestStatus.PENDING;
        },
        isTemplateRetrieveRequestFetching: (state, id: string) => {
            return state.templateRetrieveRequests[id] === APIRequestStatus.FETCHING;
        },
        isTemplateRetrieveRequestFetched: (state, id: string) => {
            return state.templateRetrieveRequests[id] === APIRequestStatus.FETCHED;
        },
        isTemplateRetrieveRequestFailed: (state, id: string) => {
            return state.templateRetrieveRequests[id] === APIRequestStatus.FAILED;
        },

        isSceneDesignsListRequestPending: (state, id: string) => {
            return state.sceneDesignsListRequest[id] === APIRequestStatus.PENDING;
        },
        isSceneDesignsListRequestFetching: (state, id: string) => {
            return state.sceneDesignsListRequest[id] === APIRequestStatus.FETCHING;
        },
        isSceneDesignsListRequestFetched: (state, id: string) => {
            return state.sceneDesignsListRequest[id] === APIRequestStatus.FETCHED;
        },
        isSceneDesignsListRequestFailed: (state, id: string) => {
            return state.sceneDesignsListRequest[id] === APIRequestStatus.FAILED;
        },

        isSceneDesignRetrieveRequestPending: (state, id: string) => {
            return state.sceneDesignRetrieveRequests[id] === APIRequestStatus.PENDING;
        },
        isSceneDesignRetrieveRequestFetching: (state, id: string) => {
            return state.sceneDesignRetrieveRequests[id] === APIRequestStatus.FETCHING;
        },
        isSceneDesignRetrieveRequestFetched: (state, id: string) => {
            return state.sceneDesignRetrieveRequests[id] === APIRequestStatus.FETCHED;
        },
        isSceneDesignRetrieveRequestFailed: (state, id: string) => {
            return state.sceneDesignRetrieveRequests[id] === APIRequestStatus.FAILED;
        },

        isDisplayGroupsListRequestPending: (state, id: string) => {
            return state.displayGroupsListRequest[id] === APIRequestStatus.PENDING;
        },
        isDisplayGroupsListRequestFetching: (state, id: string) => {
            return state.displayGroupsListRequest[id] === APIRequestStatus.FETCHING;
        },
        isDisplayGroupsListRequestFetched: (state, id: string) => {
            return state.displayGroupsListRequest[id] === APIRequestStatus.FETCHED;
        },
        isDisplayGroupsListRequestFailed: (state, id: string) => {
            return state.displayGroupsListRequest[id] === APIRequestStatus.FAILED;
        },
        getDisplayGroupsListRequestStatus: (state, id: string) => {
            return state.displayGroupsListRequest[id] ?? null;
        },
        getSceneDesignsListRequestStatus: (state, id: string) => {
            return state.sceneDesignsListRequest[id] ?? null;
        },
        getTemplateRetrieveRequestStatus: (state, id: string) => {
            return state.templateRetrieveRequests[id] ?? null;
        },
    },
});

export const {
    setTemplatesListRequestStatus,
    setTemplateCategoriesListRequestStatus,
    setTemplateRetrieveRequestStatus,
    setSceneDesignsListRequestStatus,
    setSceneDesignRetrieveRequestStatus,
    setDisplayGroupsListRequestStatus,
} = loadingInfoSlice.actions;

export const {
    isTemplatesListRequestPending,
    isTemplatesListRequestFetching,
    isTemplatesListRequestFetched,
    isTemplatesListRequestFailed,
    isTemplateCategoriesListRequestFetching,
    isTemplateCategoriesListRequestFetched,
    isTemplateRetrieveRequestPending,
    isTemplateRetrieveRequestFetching,
    isTemplateRetrieveRequestFetched,
    isTemplateRetrieveRequestFailed,
    isSceneDesignsListRequestPending,
    isSceneDesignsListRequestFetching,
    isSceneDesignsListRequestFetched,
    isSceneDesignsListRequestFailed,
    isSceneDesignRetrieveRequestPending,
    isSceneDesignRetrieveRequestFetching,
    isSceneDesignRetrieveRequestFetched,
    isSceneDesignRetrieveRequestFailed,
    isDisplayGroupsListRequestPending,
    isDisplayGroupsListRequestFetching,
    isDisplayGroupsListRequestFetched,
    isDisplayGroupsListRequestFailed,
    getDisplayGroupsListRequestStatus,
    getSceneDesignsListRequestStatus,
    getTemplateRetrieveRequestStatus,
} = loadingInfoSlice.getSelectors((state: RootState) => state.templates.loadingInfo);

export default loadingInfoSlice;
